import { z } from "zod";
import type { CSSProperties } from "react";

/**
 * Per-offering appearance. Deliberately two knobs:
 *
 *   surface — what the page sits on. `oatmeal` is the house default,
 *             `white` for sites that are, and `transparent` for the embed,
 *             where it lets the host page's own background show through.
 *   accent  — one brand colour. Drives `--primary` (the Register button),
 *             `--ring` (focus) and link colour. Button text is derived from
 *             the accent's luminance so a pale brand yellow gets dark text.
 *
 * Everything else on the page keeps the house tokens: the muted, border and
 * secondary tints are quiet enough never to fight a brand colour.
 */

export const THEME_SURFACES = ["oatmeal", "white", "transparent"] as const;
export type ThemeSurface = (typeof THEME_SURFACES)[number];

export const THEME_SURFACE_LABELS: Record<ThemeSurface, string> = {
  oatmeal: "Oatmeal",
  white: "White",
  transparent: "Transparent",
};

/**
 * Six-digit hex only. The value is rendered straight into a `style`
 * attribute, so this regex is the whole defence against CSS injection —
 * keep it strict (no shorthand, no alpha, no named colours).
 */
export const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

export const offeringThemeSchema = z.object({
  surface: z.enum(THEME_SURFACES).optional(),
  accent: z.string().regex(HEX_COLOUR, "Use a hex colour like #cc5533").nullable().optional(),
});

export type OfferingTheme = z.infer<typeof offeringThemeSchema>;

export const DEFAULT_THEME: OfferingTheme = {};

/** Coerce a jsonb column (or anything) into a theme; garbage → defaults. */
export function parseTheme(raw: unknown): OfferingTheme {
  const parsed = offeringThemeSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : DEFAULT_THEME;
}

export function themeSurface(theme: OfferingTheme | null | undefined): ThemeSurface {
  return theme?.surface ?? "oatmeal";
}

export function isDefaultTheme(theme: OfferingTheme | null | undefined): boolean {
  return themeSurface(theme) === "oatmeal" && !theme?.accent;
}

/** WCAG relative luminance of a #rrggbb colour, 0 (black) → 1 (white). */
export function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = channel((n >> 16) & 0xff);
  const g = channel((n >> 8) & 0xff);
  const b = channel(n & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Text colour for something painted in `hex`. The 0.179 threshold is where
 * white and near-black text have equal WCAG contrast against the background,
 * so whichever side we land on is the higher-contrast choice.
 */
export function foregroundFor(hex: string): string {
  return relativeLuminance(hex) > 0.179
    ? "oklch(0.20 0.012 55)" // house ink
    : "oklch(0.985 0.005 82)"; // house paper
}

// Neutral (unwarmed) tints for the white and transparent surfaces. The house
// values in globals.css carry a warm cast that reads as dirt on pure white.
const NEUTRAL_TINTS = {
  "--card": "#ffffff",
  "--popover": "#ffffff",
  "--secondary": "oklch(0.955 0 0)",
  "--accent": "oklch(0.955 0 0)",
  "--muted": "oklch(0.96 0 0)",
  "--muted-foreground": "oklch(0.48 0 0)",
  "--border": "oklch(0.90 0 0)",
  "--input": "oklch(0.90 0 0)",
} as const;

/**
 * Inline CSS custom-property overrides for a theme. Apply to the outermost
 * element of the page (the `<article>` in OfferingPreview); every Tailwind
 * token beneath — bg-background, bg-card, bg-primary, ring-ring — picks them
 * up through the `--color-*: var(--*)` mapping in globals.css.
 *
 * Returns `undefined` for the default theme so the house stylesheet is left
 * entirely alone.
 */
export function themeCssVars(
  theme: OfferingTheme | null | undefined
): CSSProperties | undefined {
  if (!theme || isDefaultTheme(theme)) return undefined;
  const vars: Record<string, string> = {};

  const surface = themeSurface(theme);
  if (surface === "white") {
    vars["--background"] = "#ffffff";
    Object.assign(vars, NEUTRAL_TINTS);
  } else if (surface === "transparent") {
    vars["--background"] = "transparent";
    Object.assign(vars, NEUTRAL_TINTS);
  }

  // Re-check the regex here as well as in the schema: a row written before
  // validation existed, or by hand, must still never reach a style attribute.
  if (theme.accent && HEX_COLOUR.test(theme.accent)) {
    vars["--primary"] = theme.accent;
    vars["--primary-foreground"] = foregroundFor(theme.accent);
    vars["--ring"] = theme.accent;
  }

  return vars as CSSProperties;
}

/**
 * What the document itself should be painted, behind the article. The
 * article only covers its own box, so a short page or the platform footer
 * would otherwise show the oatmeal body through a white theme.
 *
 *   embed=true  transparent → transparent (the host page shows through)
 *   embed=false transparent → white (there is no host page to show)
 *   oatmeal → undefined: leave the stylesheet's body colour alone
 */
export function documentBackground(
  theme: OfferingTheme | null | undefined,
  { embed }: { embed: boolean }
): string | undefined {
  const surface = themeSurface(theme);
  if (surface === "oatmeal") return undefined;
  if (surface === "transparent") return embed ? "transparent" : "#ffffff";
  return "#ffffff";
}
